import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { useRights } from "../../lib/useRights";
import { api } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import { EmptyYacht } from "./Inventory";
import { Download, FileText, Gauge } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function Reports() {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { can } = useRights();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(null);

  useEffect(() => {
    if (!activeYacht?.id) { setData(null); setHours(null); return; }
    api.get(`/reports/summary?yacht_id=${activeYacht.id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    api.get(`/reports/running-hours?yacht_id=${activeYacht.id}`)
      .then((r) => setHours(r.data))
      .catch(() => setHours(null));
    // Use the yacht id (not the whole object) so YachtContext refreshes
    // don't cause needless refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeYacht?.id]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ["Metric", "Value"],
      ["Inventory items", data.totals.inventory_items],
      ["Maintenance tasks", data.totals.maintenance_jobs],
      ["Petty cash entries", data.totals.petty_cash_entries],
      ["Maintenance cost (completed)", data.totals.maintenance_cost],
      ["Maintenance cost · offline-tracked (completed)", data.totals.maintenance_cost_offline ?? 0],
      ["Petty cash expense (approved)", data.totals.petty_cash_expense],
      ["Petty cash top-up (approved)", data.totals.petty_cash_topup],
      ["Prepayments paid", data.totals.prepay_paid_total ?? 0],
      ["Prepay balances due", data.totals.prepay_balance_due_total ?? 0],
    ];
    Object.entries(data.maintenance_by_status).forEach(([k, v]) => rows.push([`Maintenance · ${k}`, v]));
    if ((data.spend_by_month || []).length > 0) {
      rows.push([]);
      rows.push(["Monthly spend trend", ""]);
      rows.push(["Month", "Total"]);
      data.spend_by_month.forEach((m) => rows.push([m.month, m.total]));
      rows.push([]);
      rows.push(["Monthly spend by category", ""]);
      rows.push(["Month", "Category", "Amount"]);
      data.spend_by_month.forEach((m) =>
        Object.entries(m.by_category || {}).forEach(([c, v]) => rows.push([m.month, c, v]))
      );
    }
    if ((data.spend_by_supplier || []).length > 0) {
      rows.push([]);
      rows.push(["Spend by supplier", ""]);
      rows.push(["Supplier", "Jobs", "Total"]);
      data.spend_by_supplier.forEach((s) => rows.push([s.supplier, s.jobs, s.total]));
    }
    if (hours && hours.rows && hours.rows.length > 0) {
      rows.push([]);
      rows.push(["Running hours", ""]);
      rows.push(["Unit", "Group", "Hours", "Next service", "Due in (h)", "Status"]);
      hours.rows.forEach((r) => {
        const ns = r.next_service;
        rows.push([
          [r.brand, r.type].filter(Boolean).join(" · ") || r.label,
          r.group || "",
          Number(r.hours).toFixed(1),
          ns ? `${ns.title} @ ${Number(ns.next_due_hours).toFixed(0)}h` : "",
          ns ? Number(ns.hours_remaining).toFixed(1) : "",
          r.status,
        ]);
      });
    }
    const csv = rows.map((r) => r.map((cell) => {
      const s = String(cell ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeYacht.name}-report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Opens a new window with a print-styled report and triggers the browser's
   * native print dialog. The user picks "Save as PDF" (every modern browser
   * has this built-in) and downloads. No dependency, no backend round-trip,
   * works on desktop and mobile, lets the user keep the file locally.
   */
  const exportPDF = () => {
    if (!data) return;
    const html = buildReportHtml(activeYacht, data, hours);
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) {
      // Pop-up blocked — fall back to opening as a blob in the current tab.
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Give the new window a beat to paint before triggering print.
    w.onload = () => {
      try { w.focus(); w.print(); } catch { /* user can press Cmd/Ctrl-P */ }
    };
  };

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("reports.title")}
        right={
          can("report:export") ? (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={exportCSV} disabled={!data} data-testid="export-csv">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={exportPDF} disabled={!data} className="bg-ink hover:bg-black" data-testid="export-pdf">
              <FileText className="h-4 w-4 mr-2" /> Export PDF
            </Button>
          </div>
          ) : null
        }
      />

      {!data ? (
        <div className="mt-8 text-sm text-zinc-500">{t("common.loading")}</div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Inventory items" v={data.totals.inventory_items} />
            <Stat label="Maintenance tasks" v={data.totals.maintenance_jobs} />
            <Stat label="Petty cash entries" v={data.totals.petty_cash_entries} />
            <Stat label="Maintenance cost" v={`${data.totals.maintenance_cost.toFixed(2)}`} />
            <Stat
              label="Offline-tracked spend"
              v={`${(data.totals.maintenance_cost_offline ?? 0).toFixed(2)}`}
              note="Self-managed supplier jobs — included in maintenance cost"
              testId="stat-offline-spend"
            />
            <Stat label="Petty cash expense" v={`${data.totals.petty_cash_expense.toFixed(2)}`} />
            <Stat label="Petty cash top-up" v={`${data.totals.petty_cash_topup.toFixed(2)}`} />
            <Stat
              label="Prepayments paid"
              v={`${(data.totals.prepay_paid_total ?? 0).toFixed(2)}`}
              note="Upfront payments already made on supplier jobs"
              testId="stat-prepay-paid"
            />
            <Stat
              label="Prepay balances due"
              v={`${(data.totals.prepay_balance_due_total ?? 0).toFixed(2)}`}
              note="Still owed on jobs with a paid prepayment"
              testId="stat-prepay-balance"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-md border border-zinc-200 bg-white p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Tasks by status</div>
              <div className="mt-3 space-y-2 text-sm">
                {Object.entries(data.maintenance_by_status).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                ))}
                {Object.keys(data.maintenance_by_status).length === 0 && (
                  <div className="text-zinc-500">No data.</div>
                )}
              </div>
            </div>

            <div className="rounded-md border border-zinc-200 bg-white p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Low stock</div>
              {data.low_stock.length === 0 ? (
                <div className="mt-3 text-sm text-zinc-500">No low stock items.</div>
              ) : (
                <ul className="mt-3 divide-y divide-zinc-200 text-sm">
                  {data.low_stock.map((it, i) => (
                    <li key={it.id ?? `ls-${i}`} className="py-2 flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate">{it.item_name || it.name}</span>
                      <span className="shrink-0 font-mono text-xs text-amber-700">{it.quantity}/{it.min_stock}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-md border border-zinc-200 bg-white p-5" data-testid="spend-trend-card">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Monthly spend trend</div>
            {(data.spend_by_month || []).length === 0 ? (
              <div className="mt-3 text-sm text-zinc-500">No realized spend yet.</div>
            ) : (() => {
              const months = data.spend_by_month;
              const max = Math.max(...months.map((x) => x.total)) || 1;
              // Top 5 categories overall get their own colour; the rest fold
              // into "Other" to keep the stack readable.
              const catTotals = {};
              months.forEach((m) => Object.entries(m.by_category || {}).forEach(([c, v]) => { catTotals[c] = (catTotals[c] || 0) + v; }));
              const topCats = Object.entries(catTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([c]) => c);
              const hasOther = Object.keys(catTotals).some((c) => !topCats.includes(c));
              const palette = ["#18181b", "#0d9488", "#b45309", "#4338ca", "#be123c", "#a1a1aa"];
              const cats = hasOther ? [...topCats, "__other"] : topCats;
              const colorOf = (c) => palette[cats.indexOf(c)] ?? palette[5];
              const labelOf = (c) => (c === "__other" ? "Other" : c.replace(/_/g, " ").replace(/^\w/, (ch) => ch.toUpperCase()));
              const segsFor = (m) => {
                const segs = {};
                Object.entries(m.by_category || {}).forEach(([c, v]) => {
                  const key = topCats.includes(c) ? c : "__other";
                  segs[key] = (segs[key] || 0) + v;
                });
                return cats.filter((c) => segs[c] > 0).map((c) => ({ cat: c, value: segs[c] }));
              };
              return (
                <>
                  <div className="mt-3 h-44 flex items-end gap-1.5" data-testid="spend-trend-bars">
                    {months.map((r) => {
                      const label = new Date(`${r.month}-01T00:00:00`).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
                      return (
                        <div key={r.month} className="flex-1 h-full flex flex-col justify-end items-center min-w-0" data-testid={`spend-month-${r.month}`} title={`${r.month}: ${r.total.toFixed(2)}`}>
                          <span className="text-[9px] font-mono text-zinc-500 mb-0.5">
                            {r.total >= 1000 ? `${(r.total / 1000).toFixed(1)}k` : r.total.toFixed(0)}
                          </span>
                          <div className="w-full max-w-[40px] flex flex-col-reverse rounded-t-sm overflow-hidden" style={{ height: `${Math.max((r.total / max) * 80, 2)}%` }}>
                            {segsFor(r).map((s) => (
                              <div
                                key={s.cat}
                                style={{ height: `${(s.value / r.total) * 100}%`, backgroundColor: colorOf(s.cat) }}
                                title={`${labelOf(s.cat)}: ${s.value.toFixed(2)}`}
                              />
                            ))}
                          </div>
                          <span className="mt-1 text-[10px] text-zinc-500 truncate">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="spend-trend-legend">
                    {cats.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600">
                        <span className="h-2.5 w-2.5 rounded-sm inline-block" style={{ backgroundColor: colorOf(c) }} />
                        {labelOf(c)}
                        <span className="font-mono text-zinc-400">
                          {(c === "__other"
                            ? Object.entries(catTotals).filter(([k]) => !topCats.includes(k)).reduce((a, [, v]) => a + v, 0)
                            : catTotals[c]
                          ).toFixed(2)}
                        </span>
                      </span>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          <div className="mt-4 rounded-md border border-zinc-200 bg-white p-5" data-testid="spend-by-supplier-card">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Spend by supplier</div>
            {(data.spend_by_supplier || []).length === 0 ? (
              <div className="mt-3 text-sm text-zinc-500">No realized supplier spend yet.</div>
            ) : (
              <div className="mt-3 space-y-2.5 text-sm">
                {data.spend_by_supplier.map((s) => {
                  const max = data.spend_by_supplier[0].total || 1;
                  return (
                    <div key={s.supplier} data-testid={`spend-row-${s.supplier}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0 truncate">
                          {s.supplier}
                          <span className="ml-2 text-[11px] text-zinc-400">{s.jobs} job{s.jobs === 1 ? "" : "s"}</span>
                        </span>
                        <span className="shrink-0 font-mono">{Number(s.total).toFixed(2)}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                        <div className="h-full rounded-full bg-zinc-800" style={{ width: `${Math.max((s.total / max) * 100, 2)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <RunningHoursPanel hours={hours} />
        </>
      )}
    </div>
  );
}

function RunningHoursPanel({ hours }) {
  if (!hours) return null;
  const rows = hours.rows || [];
  return (
    <div className="mt-4 rounded-md border border-zinc-200 bg-white p-5" data-testid="running-hours-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-zinc-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Running hours</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
            {hours.summary.overdue} overdue
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            {hours.summary.soon} due soon
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {hours.summary.ok} ok
          </span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-3 text-sm text-zinc-500">
          No hour-tracked units on this yacht yet. Add engines, generators or stabilizers in the Yacht spec.
        </div>
      ) : (
        <>
        {/* MOBILE — unit cards */}
        <div className="mt-3 space-y-2 md:hidden" data-testid="running-hours-cards">
          {rows.map((r, i) => (
            <RunningHoursCard key={r.id ?? `rhc-${i}`} row={r} />
          ))}
        </div>
        {/* DESKTOP — table */}
        <div className="mt-3 overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="py-2 pr-3 font-medium">Unit</th>
                <th className="py-2 pr-3 font-medium">Group</th>
                <th className="py-2 pr-3 font-medium text-right">Hours</th>
                <th className="py-2 pr-3 font-medium">Next service</th>
                <th className="py-2 pr-3 font-medium text-right">Due in (h)</th>
                <th className="py-2 pr-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <RunningHoursRow key={r.id ?? `rh-${i}`} row={r} />
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

const KIND_LABEL = {
  engine: "Engine",
  generator: "Generator",
  stabilizer: "Stabilizer",
  tender_engine: "Tender",
};

function RunningHoursRow({ row }) {
  const next = row.next_service;
  const tone = STATUS_TONE[row.status];
  return (
    <tr className="border-t border-zinc-100" data-testid={`running-hours-row-${row.id}`}>
      <td className="py-2 pr-3">
        <div className="text-[11px] uppercase tracking-wider text-zinc-400">{KIND_LABEL[row.kind] || row.kind}</div>
        <div className="font-medium">{[row.brand, row.type].filter(Boolean).join(" · ") || row.label}</div>
      </td>
      <td className="py-2 pr-3 text-zinc-600 text-xs">{row.group || "—"}</td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(row.hours).toFixed(1)}</td>
      <td className="py-2 pr-3">
        {next ? (
          <div>
            <div className="text-sm">{next.title}</div>
            <div className="text-[11px] text-zinc-500">@ {Number(next.next_due_hours).toFixed(0)}h</div>
          </div>
        ) : <span className="text-zinc-400 text-xs">—</span>}
      </td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums">
        {next ? (
          <span className={next.hours_remaining < 0 ? "text-rose-600 font-medium" : ""}>
            {next.hours_remaining > 0 ? `${next.hours_remaining.toFixed(1)}` : `${next.hours_remaining.toFixed(1)}`}
          </span>
        ) : <span className="text-zinc-400">—</span>}
      </td>
      <td className="py-2 pr-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.cls}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
          {tone.label}
        </span>
      </td>
    </tr>
  );
}

function RunningHoursCard({ row }) {
  const next = row.next_service;
  const tone = STATUS_TONE[row.status];
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={`running-hours-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">
            {KIND_LABEL[row.kind] || row.kind}{row.group ? ` · ${row.group}` : ""}
          </div>
          <div className="font-medium text-sm truncate">{[row.brand, row.type].filter(Boolean).join(" · ") || row.label}</div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.cls}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
          {tone.label}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600">
        <span className="font-mono tabular-nums">{Number(row.hours).toFixed(1)} h</span>
        {next ? (
          <>
            <span className="truncate">{next.title} @ {Number(next.next_due_hours).toFixed(0)}h</span>
            <span className={`font-mono tabular-nums ${next.hours_remaining < 0 ? "text-rose-600 font-medium" : ""}`}>
              due in {next.hours_remaining.toFixed(1)} h
            </span>
          </>
        ) : <span className="text-zinc-400">no service scheduled</span>}
      </div>
    </div>
  );
}

const STATUS_TONE = {  overdue: { label: "Overdue", cls: "bg-rose-50 text-rose-700 ring-1 ring-rose-100", dot: "bg-rose-500" },
  soon:    { label: "Due soon", cls: "bg-amber-50 text-amber-800 ring-1 ring-amber-100", dot: "bg-amber-500" },
  ok:      { label: "OK", cls: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100", dot: "bg-emerald-500" },
};

function Stat({ label, v, note, testId }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="mt-2 font-display text-2xl font-medium">{v}</div>
      {note && <div className="mt-1 text-[11px] text-zinc-400">{note}</div>}
    </div>
  );
}

/**
 * Returns a fully self-contained HTML document for the print/PDF window.
 * Styled inline so it works the same regardless of the calling page's CSS,
 * with an @page rule that sets A4 margins for a clean printed result.
 */
function buildReportHtml(yacht, data, hours) {
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
  const nowStr = new Date().toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const totals = data.totals || {};
  const byStatus = Object.entries(data.maintenance_by_status || {});
  const lowStock = data.low_stock || [];
  const totalsRows = [
    ["Inventory items",                totals.inventory_items],
    ["Maintenance tasks",               totals.maintenance_jobs],
    ["Petty cash entries",             totals.petty_cash_entries],
    ["Maintenance cost (completed)",   totals.maintenance_cost?.toFixed(2)],
    ["Maintenance cost · offline-tracked (completed)", (totals.maintenance_cost_offline ?? 0).toFixed(2)],
    ["Petty cash expense (approved)",  totals.petty_cash_expense?.toFixed(2)],
    ["Petty cash top-up (approved)",   totals.petty_cash_topup?.toFixed(2)],
  ];
  return `<!doctype html><html><head>
<meta charset="utf-8" />
<title>${esc(yacht.name)} — Seavanto report</title>
<style>
  @page { size: A4; margin: 18mm 16mm 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Inter", "Helvetica Neue", Arial, sans-serif;
         color: #18181b; margin: 0; padding: 24px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .brand { font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; color: #71717a; font-weight: 600; }
  h1    { font-size: 26px; letter-spacing: -0.01em; margin: 4px 0 0 0; }
  .meta { color: #52525b; font-size: 12px; margin-top: 4px; }
  h2    { font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase;
          color: #52525b; margin: 28px 0 10px 0; border-bottom: 1px solid #e4e4e7; padding-bottom: 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  td    { padding: 7px 4px; border-bottom: 1px solid #f4f4f5; vertical-align: top; }
  tr:last-child td { border-bottom: none; }
  td.value { text-align: right; font-variant-numeric: tabular-nums; font-weight: 500; }
  .row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; page-break-inside: avoid; }
  .panel { border: 1px solid #e4e4e7; border-radius: 8px; padding: 14px 16px; }
  .empty { color: #a1a1aa; font-style: italic; font-size: 12.5px; }
  .footer { margin-top: 32px; font-size: 10.5px; color: #a1a1aa; text-align: center; }
  @media print { .no-print { display: none !important; } }
  .actions { margin-top: 24px; }
  .actions button {
    appearance: none; border: 1px solid #e4e4e7; background: #18181b; color: white;
    font-weight: 600; font-size: 13px; padding: 9px 16px; border-radius: 6px; cursor: pointer;
  }
  .actions .secondary { background: white; color: #18181b; margin-left: 8px; }
</style>
</head>
<body>
  <div class="brand">Seavanto</div>
  <h1>${esc(yacht.name)} — Report</h1>
  <div class="meta">Generated ${esc(nowStr)}</div>

  <h2>Totals</h2>
  <table>
    ${totalsRows.map(([k, v]) =>
      `<tr><td>${esc(k)}</td><td class="value">${esc(v ?? "—")}</td></tr>`
    ).join("")}
  </table>

  <div class="row-2">
    <div>
      <h2>Tasks by status</h2>
      ${byStatus.length === 0
        ? `<div class="empty">No maintenance data.</div>`
        : `<table>${byStatus.map(([k, v]) =>
            `<tr><td style="text-transform:capitalize">${esc(k.replace(/_/g, " "))}</td><td class="value">${esc(v)}</td></tr>`
          ).join("")}</table>`}
    </div>
    <div>
      <h2>Low stock items</h2>
      ${lowStock.length === 0
        ? `<div class="empty">No low stock items.</div>`
        : `<table>${lowStock.map((it) =>
            `<tr><td>${esc(it.item_name || it.name)}</td><td class="value" style="color:#b45309">${esc(it.quantity)} / ${esc(it.min_stock)}</td></tr>`
          ).join("")}</table>`}
    </div>
  </div>

  ${hours && hours.rows && hours.rows.length > 0 ? `
  <h2>Running hours</h2>
  <table>
    <tr>
      <td style="font-weight:600">Unit</td>
      <td style="font-weight:600">Group</td>
      <td class="value" style="font-weight:600">Hours</td>
      <td style="font-weight:600">Next service</td>
      <td class="value" style="font-weight:600">Due in (h)</td>
      <td style="font-weight:600">Status</td>
    </tr>
    ${hours.rows.map((r) => {
      const ns = r.next_service;
      const tone = r.status === "overdue" ? "#b91c1c" : r.status === "soon" ? "#b45309" : "#047857";
      const statusLabel = r.status === "overdue" ? "Overdue" : r.status === "soon" ? "Due soon" : "OK";
      return `<tr>
        <td>${esc([r.brand, r.type].filter(Boolean).join(" · ") || r.label)}</td>
        <td>${esc(r.group || "—")}</td>
        <td class="value">${Number(r.hours).toFixed(1)}</td>
        <td>${esc(ns ? `${ns.title} @ ${Number(ns.next_due_hours).toFixed(0)}h` : "—")}</td>
        <td class="value">${ns ? Number(ns.hours_remaining).toFixed(1) : "—"}</td>
        <td style="color:${tone};font-weight:600">${statusLabel}</td>
      </tr>`;
    }).join("")}
  </table>
  ` : ""}

  ${(data.supplier_jobs || []).length > 0 ? `
  <h2>Supplier jobs &amp; scope</h2>
  ${data.supplier_jobs.map((j) => `
    <div class="panel" style="margin-bottom:10px; page-break-inside: avoid;">
      <div style="display:flex; justify-content:space-between; gap:12px; font-size:13px;">
        <strong>${esc(j.title || "—")}</strong>
        <span style="white-space:nowrap; font-weight:600;">${esc(j.currency || "")} ${esc(j.amount ?? "—")}</span>
      </div>
      <div style="font-size:11px; color:#52525b; margin-top:2px;">
        ${esc(j.supplier_name || "")} · status: ${esc((j.status || "").replace(/_/g, " "))}${j.track_offline ? ` · <span style="color:#71717a; font-weight:600;">tracked offline</span>` : ""}
        ${j.start_date ? ` · work ${esc((j.start_date || "").slice(0, 10))}${j.end_date ? ` → ${esc((j.end_date || "").slice(0, 10))}` : ""}` : ""}
      </div>
      ${(j.scope_items || []).length > 0 ? `
      <table style="margin-top:6px;">
        ${j.scope_items.map((s, i) =>
          `<tr><td style="width:22px; color:#a1a1aa;">${i + 1}.</td><td>${esc(s.text)}</td><td class="value">${s.price != null ? `${esc(j.currency || "")} ${esc(s.price)}` : ""}</td></tr>`
        ).join("")}
      </table>` : `<div class="empty" style="margin-top:6px;">No itemised scope provided.</div>`}
    </div>`).join("")}
  ` : ""}

  <div class="footer">Seavanto · ${esc(yacht.name)} · ${esc(nowStr)}</div>

  <div class="actions no-print">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>
</body></html>`;
}
